import { useMemo, useRef } from "react";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

import { IUser } from "Model/Storage/IUser";
import { hasNoElements } from "Utils/Array";
import { fitTextListToContent } from "Utils/String";
import { FilterToolTipContent } from "Components/Generic/FilterToolTipContent";
import { WithTranslator } from "Components/Generic/Translator";
import { useResizeObserver } from "Hooks/UseResizeObserver";
import { IRendererProps } from "../IRendererProps";
import { useUserRendererStyles } from "./UseUserRendererStyles";
import { TooltipDirection, TooltipCloseDelay, TooltipOpenDelay } from "../TooltipSettings";

export const UserRenderer = WithTranslator((props: IRendererProps): JSX.Element => {
  const { item, fieldName, styles } = props;
  const { styleSet, font } = useUserRendererStyles(styles);
  const ref = useRef<HTMLDivElement>();
  const width = useResizeObserver(ref);

  const users: IUser[] = item?.[`@users`];
  const [userNames, userNamesTooltip] = useMemo(() => {
    if (ref.current == null || hasNoElements(users)) {
      return [null, null];
    }

    return [
      fitTextListToContent(
        users.map(({ displayName }) => displayName),
        width,
        font
      ),
      users.map(({ displayName, name }) => (
        <FilterToolTipContent key={name} fieldName={fieldName} value={`${displayName} (${name})`} rawValue={name} />
      ))
    ];
  }, [fieldName, font, users, width]);

  return (
    <TooltipHost
      closeDelay={TooltipCloseDelay}
      content={userNamesTooltip}
      delay={TooltipOpenDelay}
      directionalHint={TooltipDirection}
      hostClassName={styleSet.host}
    >
      <div className={styleSet.root} ref={ref}>
        <Text>{userNames}</Text>
      </div>
    </TooltipHost>
  );
});
